import apiClient from './ApiClient';

export class AuthRepository {
	constructor(client) {
		this.client = client;
		this.basePath = '/api/auth';
	}

	/**
	 * Register a new user account
	 * @param {Object} payload - Registration data
	 * @param {string} payload.email - User email
	 * @param {string} payload.password - User password
	 * @param {string} payload.firstName - User first name
	 * @param {string} payload.lastName - User last name
	 * @param {string} payload.companyName - Company name
	 * @param {string} payload.phoneNumber - Phone number
	 * @param {string} [payload.avatarUrl] - Avatar URL (optional)
	 * @param {string} [payload.companyDescription] - Company description (optional)
	 * @returns {Promise<{userId: string}>}
	 */
	async register(payload) {
		return this.client.post(`${this.basePath}/register`, payload);
	}

	/**
	 * Login user and get access tokens
	 * @param {Object} credentials
	 * @param {string} credentials.email - User email
	 * @param {string} credentials.password - User password
	 * @returns {Promise<{accessToken: string, refreshToken: string, accessTokenExpiresAt: string, refreshTokenExpiresAt: string}>}
	 */
	async login(credentials) {
		return this.client.post(`${this.basePath}/login`, credentials);
	}

	/**
	 * Refresh access token using refresh token
	 * @param {string} refreshToken - Refresh token
	 * @returns {Promise<{accessToken: string, refreshToken: string, accessTokenExpiresAt: string, refreshTokenExpiresAt: string}>}
	 */
	async refreshToken(refreshToken) {
		return this.client.post(`${this.basePath}/refresh-token`, { refreshToken });
	}

	/**
	 * Get current authenticated user info
	 * @returns {Promise<Object>} User object with company details
	 */
	async whoami({ skipAuthRefresh = false } = {}) {
		return this.client.request(`${this.basePath}/whoami`, { skipAuthRefresh });
	}

	/**
	 * Logout and revoke refresh token
	 * @param {string} refreshToken - Refresh token to revoke
	 * @returns {Promise<{message: string}>}
	 */
	async logout(refreshToken) {
		return this.client.post(`${this.basePath}/logout`, { refreshToken });
	}

	/**
     * Request a password reset link
     * @param {string} email - The email address of the user
     * @returns {Promise<Object>}
     */
    async forgotPassword(email) {
        return this.client.post(`${this.basePath}/forgot-password`, { email });
    }

    /**
     * Reset the user password using a token
     * @param {string} email - The email address from query params
     * @param {string} token - The reset token from query params
     * @param {string} newPassword - The new password chosen by the user
     * @returns {Promise<Object>}
     */
    async resetPassword({ email, token, newPassword }) {
        return this.client.post(`${this.basePath}/reset-password`, { 
            email, 
            token, 
            newPassword 
        });
    }
}

export default new AuthRepository(apiClient);
